import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ComparisonSlider } from '../UI/ComparisonSlider';
import type { TransformationProofContent } from './landingProofContent';

interface LandingTransformationProofProps {
  content: TransformationProofContent;
  onGetStarted: () => void;
}

export function LandingTransformationProof({
  content,
  onGetStarted,
}: LandingTransformationProofProps) {
  return (
    <section className="relative overflow-hidden border-t border-stone bg-gradient-to-b from-alabaster via-white to-alabaster px-6 py-24 md:py-32">
      <div className="absolute left-1/2 top-12 h-56 w-[52rem] -translate-x-1/2 rounded-full bg-terracotta/10 blur-3xl" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[0.92fr_1.08fr]">
        <div className="space-y-8">
          <div className="inline-flex items-center rounded-full border border-stone bg-white/80 px-4 py-2 text-xs font-mono uppercase tracking-[0.28em] text-sand shadow-soft">
            {content.eyebrow}
          </div>
          <div className="space-y-5">
            <h2 className="max-w-2xl text-4xl font-serif font-light tracking-tight text-charcoal md:text-6xl">
              {content.title}
            </h2>
            <p className="max-w-xl text-lg font-light leading-relaxed text-sand md:text-xl">
              {content.description}
            </p>
            <p className="text-sm font-mono uppercase tracking-[0.24em] text-olive">
              {content.supporting}
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            {content.proofPoints.map((point) => (
              <div
                key={point.label}
                className="rounded-3xl border border-stone bg-white/85 p-5 shadow-soft"
              >
                <div className="mb-2 text-xs font-mono uppercase tracking-[0.24em] text-sand">
                  {point.label}
                </div>
                <div className="text-sm font-light leading-relaxed text-charcoal/80">
                  {point.value}
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <button
              onClick={onGetStarted}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-olive px-8 py-4 text-base font-medium text-alabaster transition-all duration-500 hover:-translate-y-0.5 hover:bg-charcoal hover:shadow-lift"
            >
              Start Staging
              <ArrowRight className="h-4 w-4" />
            </button>
            <Link
              to="/virtual-staging/before-after"
              className="inline-flex items-center gap-2 border-b border-sand/30 pb-1 text-base font-light text-sand transition-all duration-300 hover:border-terracotta/50 hover:text-terracotta"
            >
              Explore more before &amp; after proof
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        <div className="relative">
          <div className="mb-4 flex items-center justify-between gap-4">
            <div className="inline-flex items-center rounded-full border border-stone/70 bg-white/90 px-4 py-2 text-xs font-mono uppercase tracking-[0.24em] text-sand shadow-soft">
              Same room, same light
            </div>
            <div className="rounded-full bg-charcoal px-4 py-2 text-sm font-light text-alabaster shadow-soft">
              {content.stat}
            </div>
          </div>
          <div className="overflow-hidden rounded-[2rem] border border-stone/80 bg-white p-3 shadow-soft-xl">
            <ComparisonSlider
              beforeSrc={content.slider.beforeSrc}
              afterSrc={content.slider.afterSrc}
              beforeAlt={content.slider.beforeAlt}
              afterAlt={content.slider.afterAlt}
              beforeLabel={content.slider.beforeLabel}
              afterLabel={content.slider.afterLabel}
              aspectRatio="aspect-[4/3]"
              enableAutoDemo={true}
            />
          </div>
          <div className="mt-4 flex items-center justify-between gap-4 text-sm font-light text-sand">
            <span>Same architecture, two very different buyer signals.</span>
            <span>Drag to compare lived-in versus market-ready.</span>
          </div>
        </div>
      </div>
    </section>
  );
}
